/* eslint-disable max-lines */
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import Button from '@components/atoms/ui-kit/button.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import { MultiValue } from 'react-select';
import { useTranscript } from '@utils/providers/transcript-context.tsx';
import { useTranslateDocument } from '@utils/api/documents/use-translate-document.tsx';
import { useTranslation } from 'react-i18next';
import Input from '@components/atoms/ui-kit/input.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { css, StyleSheet } from 'aphrodite';
import cn from 'classnames';
import { DocumentTranslationLanguage, Option } from '@utils/types/shared.ts';
import {
  useShareDocument,
  useShareDocumentEmail
} from '@utils/api/magic-links/use-post-share-link.tsx';
import { useParams } from 'react-router-dom';
import SelectInput from '@components/atoms/ui-kit/select-input.tsx';
import ExportDocumentForm from '@components/workspace/export-document-form.tsx';
import PublishAction from '@components/publishing/publish-action.tsx';
import {
  ClipboardCopy,
  Download,
  Languages,
  Bot,
  WandSparkles,
  Mail
} from 'lucide-react';
import {
  getDocumentDirectUrl,
  triggerDownloadFromUrl
} from '@utils/api/documents/use-get-document-direct.tsx';
import { getDocumentRaw } from '@utils/api/documents/use-get-document-raw.tsx';
import { parse } from '@utils/extensions/content-disposition.extension.ts';
import saveAs from 'file-saver';
import { useEntitlements } from '@utils/providers/entitlements-context.tsx';
import { useOrganization } from '@utils/api/users/use-get-organization.tsx';
import { useGetGlossaries } from '@utils/api/glossaries/use-get-glossaries.tsx';
import { Glossary } from '@utils/types/glossary.ts';

/* eslint-disable no-restricted-syntax -- Semantic editor colors (recording/streaming status, playback cues, subtitle palette, pill foregrounds on tinted backgrounds) must stay stable across themes */

type Params = {
  sessionId: string;
  documentId: string;
};

function parseEmails(value: string): string[] {
  return value
    .split(/[\s,;\n\r]+/)
    .map(e => e.trim())
    .filter(Boolean);
}

function isValidEmail(email: string): boolean {
  // Simple but good-enough email pattern
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

type Props = {
  /** Optional AI Summary trigger — when provided, renders a 4th button in
   *  the group. The parent owns the modal state (see TranscriptSummarizeButton
   *  with `renderTrigger={false}`). */
  aiSummary?: {
    enabled: boolean;
    disabled: boolean;
    onOpen: () => void;
  };
  /** Controlled export-modal state. Supplied by the parent when the
   *  Export action is also exposed in the editor header's overflow
   *  menu (narrow widths, #2231) so the modal can be opened from
   *  outside the component's own button. Internal state is used when
   *  these props are omitted. */
  exportOpen?: boolean;
  onExportOpenChange?: (open: boolean) => void;
  /** Controlled publish-dialog state, same pattern as `exportOpen` so
   *  the editor header's overflow menu can open Publish at phone widths
   *  where the inline button is hidden (#2303). */
  publishOpen?: boolean;
  onPublishOpenChange?: (open: boolean) => void;
};

export const TranscriptExportDocument = ({
  aiSummary,
  exportOpen,
  onExportOpenChange,
  publishOpen,
  onPublishOpenChange
}: Props = {}) => {
  const { t } = useTranslation();
  const { sessionId, documentId } = useParams<Params>();
  const { documentResult, session } = useTranscript();
  const { hasFeature } = useEntitlements();

  const [modal, setModal] = useState<
    '' | 'translate' | 'docx' | 'template' | 'txt' | 'html' | 'pdf'
  >('');
  const [linkModal, setLinkModal] = useState<boolean>(false);
  const [generatingLink, setGeneratingLink] = useState<boolean>(false);
  const [link, setLink] = useState<string>('');
  const { mutateAsync, isLoading: translationIsLoading } =
    useTranslateDocument();
  const { mutate: translateAllMutate, isLoading: translateAllLoading } =
    useTranslateDocument();
  const { data: organization } = useOrganization();
  const [selectedLanguages, setSelectedLanguages] = useState<Option[]>([]);
  const [filenameInput, setFilenameInput] = useState<string>(
    session?.filename || ''
  );
  const { getColour } = useThemeContext();

  const [internalExportModal, setInternalExportModal] = useState(false);
  const exportModal = exportOpen ?? internalExportModal;
  // Accept both plain-boolean sets and the functional updater that
  // ExportDocumentForm uses — resolves the updater against the
  // current value and forwards.
  const setExportModal: React.Dispatch<
    React.SetStateAction<boolean>
  > = value => {
    const next =
      typeof value === 'function'
        ? (value as (prev: boolean) => boolean)(exportModal)
        : value;
    if (onExportOpenChange) onExportOpenChange(next);
    else setInternalExportModal(next);
  };
  const [includeSpeakers, setIncludeSpeakers] = useState(true);
  const [includeTimestamps, setIncludeTimestamps] = useState(true);

  // New state for email sharing
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [lastEmailsSentCount, setLastEmailsSentCount] = useState<number | null>(
    null
  );

  const parsedEmails = useMemo(() => parseEmails(emailInput), [emailInput]);

  // Glossary state
  const [selectedGlossaryId, setSelectedGlossaryId] = useState<number | null>(
    null
  );
  const { data: glossariesData } = useGetGlossaries();

  const matchingGlossaries = useMemo(() => {
    if (!glossariesData?.result || selectedLanguages.length === 0) return [];
    const targetCodes = selectedLanguages.map(l => l.value.toUpperCase());
    return glossariesData.result.filter(
      (g: Glossary) =>
        g.is_active && targetCodes.includes(g.target_language.toUpperCase())
    );
  }, [glossariesData, selectedLanguages]);

  const { mutate: shareDocument } = useShareDocument({
    onSuccess: data => {
      if (data && data.result.link) {
        setLinkModal(true);
        setLink(data.result.link);
        setGeneratingLink(false);
      } else {
        toast.error(t('failed-to-generate-link'));
        setGeneratingLink(false);
      }
    },
    onError: () => {
      toast.error(t('failed-to-generate-link'));
      setGeneratingLink(false);
    }
  });

  const { mutateAsync: sendMagicLinkEmail, isLoading: isSendingEmail } =
    useShareDocumentEmail();

  const styles = useMemo(
    () =>
      StyleSheet.create({
        topNavContainer: {
          borderBottom: `1px solid ${getColour('border')}`
        },
        topNavContainerColor: {
          backgroundColor: getColour('thirdLayer')
        },
        iconsColor: {
          color: getColour('icon')
        },
        userText: {
          color: getColour('normalText')
        }
      }),
    [getColour]
  );

  const options: Option[] = useMemo(
    () =>
      Object.keys(DocumentTranslationLanguage).map(
        key =>
          ({
            label: key,
            value:
              DocumentTranslationLanguage[
                key as keyof typeof DocumentTranslationLanguage
              ]
          }) as Option
      ),
    []
  );

  const handleMediaDownload = () => {
    if (!session?.media_url) {
      toast.error(t('no-media-available'));
      return;
    }
    const a = document.createElement('a');
    a.href = session.media_url;
    a.setAttribute('download', session.filename || 'media-file');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleTranslate = () => {
    if (!selectedLanguages || selectedLanguages.length === 0) {
      toast.error(t('select-at-least-one-language'));
      return;
    }
    if (!documentResult) return;

    const toastId = toast.loading(t('translating-document'));

    mutateAsync({
      sessionId: session?.session_id as string,
      documentId: documentResult.result.id as string,
      languages: selectedLanguages.map(lang => lang.value.toUpperCase()),
      filename: filenameInput,
      glossary_id: selectedGlossaryId
    })
      .then(async data => {
        toast.dismiss(toastId);
        setModal('');

        const shouldAutoDownload =
          (documentResult.result.use_plain_document &&
            !documentResult.result.plain_document_changed) ||
          false;

        if (shouldAutoDownload && data?.json && Array.isArray(data.json)) {
          for (const doc of data.json) {
            if (!doc.id || !session?.session_id) continue;
            try {
              const resp = await getDocumentDirectUrl({
                sessionId: session.session_id,
                documentId: doc.id,
                format: 'docx'
              });
              await triggerDownloadFromUrl(
                resp.result.download_url,
                resp.result.filename,
                resp.result.content_type
              );
            } catch {
              try {
                const resp = await getDocumentRaw({
                  sessionId: session.session_id,
                  documentId: doc.id,
                  format: 'docx'
                });
                const disposition = parse(
                  resp.headers['content-disposition'] || ''
                );
                const fname =
                  disposition?.parameters?.filename ||
                  doc.filename ||
                  'translated.docx';
                saveAs(resp.data, fname);
              } catch {
                toast.error(t('error-downloading-document'));
              }
            }
          }
        }
      })
      .catch(() => {
        toast.dismiss(toastId);
      });
  };

  const orgDefaultLanguages = organization?.default_translation_languages ?? [];

  const handleTranslateAll = () => {
    if (!documentResult || orgDefaultLanguages.length === 0) return;

    toast(
      t('translating-to-n-languages', {
        count: orgDefaultLanguages.length
      })
    );

    const shouldAutoDownload =
      (documentResult.result.use_plain_document &&
        !documentResult.result.plain_document_changed) ||
      false;

    translateAllMutate(
      {
        sessionId: session?.session_id as string,
        documentId: documentResult.result.id as string,
        languages: orgDefaultLanguages.map(l => l.toUpperCase()),
        filename: session?.filename,
        glossary_id: selectedGlossaryId
      },
      {
        onSuccess: async data => {
          toast.success(t('translate-all-completed'));

          if (shouldAutoDownload && data?.json && Array.isArray(data.json)) {
            for (const doc of data.json) {
              if (!doc.id || !session?.session_id) continue;
              try {
                const resp = await getDocumentDirectUrl({
                  sessionId: session.session_id,
                  documentId: doc.id,
                  format: 'docx'
                });
                await triggerDownloadFromUrl(
                  resp.result.download_url,
                  resp.result.filename,
                  resp.result.content_type
                );
              } catch {
                try {
                  const resp = await getDocumentRaw({
                    sessionId: session.session_id,
                    documentId: doc.id,
                    format: 'docx'
                  });
                  const disposition = parse(
                    resp.headers['content-disposition'] || ''
                  );
                  const fname =
                    disposition?.parameters?.filename ||
                    doc.filename ||
                    'translated.docx';
                  saveAs(resp.data, fname);
                } catch {
                  toast.error(t('error-downloading-document'));
                }
              }
            }
          }
        }
      }
    );
  };

  const handleGenerateLink = async () => {
    if (!sessionId || !documentId) return;
    setGeneratingLink(true);

    shareDocument({ sessionId, documentId });
  };

  const handleCopyLink = () => {
    navigator.clipboard
      .writeText(link)
      .then(() => {
        toast.success(t('link-copied-to-clipboard'));
      })
      .catch(() => {
        toast.error(t('failed-to-copy-the-link'));
      });
  };

  const handleEmailInputChange = (value: string) => {
    setEmailInput(value);

    if (!value.trim()) {
      setEmailError(null);
      return;
    }

    const emails = parseEmails(value);
    const invalid = emails.filter(e => !isValidEmail(e));

    if (invalid.length > 0) {
      setEmailError(
        t('invalid-email-addresses', { emails: invalid.join(', ') }) ||
          `Invalid email(s): ${invalid.join(', ')}`
      );
    } else {
      setEmailError(null);
    }
  };

  const handleSendEmail = () => {
    if (!sessionId || !documentId) return;

    const emails = parsedEmails;
    if (!emails.length) {
      setEmailError(
        t('please-enter-at-least-one-email') ||
          'Please enter at least one email address.'
      );
      return;
    }

    const invalid = emails.filter(e => !isValidEmail(e));
    if (invalid.length > 0) {
      setEmailError(
        t('invalid-email-addresses', { emails: invalid.join(', ') }) ||
          `Invalid email(s): ${invalid.join(', ')}`
      );
      return;
    }

    setEmailError(null);

    sendMagicLinkEmail({
      sessionId,
      documentId,
      emails,
      message: message.trim() || undefined
    })
      .then(data => {
        setLastEmailsSentCount(data?.result?.emails_sent?.length ?? null);
        // Keep dialog open so user can send to more people, but clear inputs
        setLinkModal(false);
        setEmailInput('');
        setMessage('');
      })
      .catch(() => {
        // Error toast handled in hook
      });
  };

  const remainingChars = 500 - message.length;

  return (
    <>
      <div
        className={cn(
          // Hide the whole button cluster below md so phones (#2231)
          // show the Export entry only via the editor header's
          // overflow menu — the modals below stay mounted so the
          // overflow action can open them via `exportOpen`.
          'hidden md:flex gap-2 content-center items-center',
          css(styles.userText)
        )}
      >
        <div className="flex items-center gap-1 flex-wrap">
          {hasFeature('translation') && (
            <Button
              size="sm"
              variant="secondary"
              icon={<Languages size={12} />}
              label={t('translate')}
              onClick={() => setModal('translate')}
              className="h-9 max-w-fit min-[1080px]:w-32 justify-center text-xs px-2!"
            />
          )}
          <Button
            size="sm"
            variant="secondary"
            icon={<Download size={12} />}
            label={t('export')}
            onClick={() => setExportModal(true)}
            className="h-9 max-w-fit min-[1080px]:w-32 justify-center text-xs px-2!"
          />
          <Button
            size="sm"
            variant="secondary"
            icon={<WandSparkles size={12} />}
            label={t('magic-link')}
            onClick={handleGenerateLink}
            disabled={documentResult?.result?.finished || generatingLink}
            loading={generatingLink}
            className="h-9 max-w-fit min-[1080px]:w-32 justify-center text-xs px-2!"
          />
          {aiSummary?.enabled && (
            <Button
              size="sm"
              variant="secondary"
              icon={<Bot size={12} />}
              label={t('ai-summary')}
              onClick={aiSummary.onOpen}
              disabled={aiSummary.disabled}
              className="h-9 max-w-fit min-[1080px]:w-32 justify-center text-xs px-2!"
            />
          )}
        </div>
      </div>
      {/* Export modal lives outside the hidden-on-narrow wrapper so
          the editor header's overflow menu (#2231) can still open it
          at phone widths via the controlled `exportOpen` prop. */}
      {sessionId && documentId && (
        <ExportDocumentForm
          modalTranscript={exportModal}
          setModalTranscript={setExportModal}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          selectedItem={session as any}
          handleMediaDownload={handleMediaDownload}
          includeTimestamps={includeTimestamps}
          setIncludeTimestamps={setIncludeTimestamps}
          includeSpeakers={includeSpeakers}
          setIncludeSpeakers={setIncludeSpeakers}
          sessionId={sessionId}
          documentId={documentId}
        />
      )}
      {/* Same reasoning for Publish — dialog mounts here so the
          overflow menu can trigger it at phone widths (#2303). */}
      {sessionId && documentId && (
        <PublishAction
          sessionId={sessionId}
          documentId={documentId}
          defaultPayloadKind="transcript"
          allowKindToggle
          fallbackLanguages={session?.language ? [session.language] : []}
          open={publishOpen}
          onOpenChange={onPublishOpenChange}
        />
      )}

      {/* Translate Modal */}
      <Modal
        isOpen={modal === 'translate'}
        onClose={() => setModal('')}
        size="medium"
        customWidth="560px"
        title={t('translate-document')}
        caption={session?.filename || t('select-languages-to-translate')}
        footerActions={[
          <Button
            onClick={() => setModal('')}
            label={t('cancel')}
            size="small"
            variant="smoke"
            key="cancel-translate"
          />,
          <Button
            onClick={handleTranslate}
            label={t('translate')}
            size="small"
            variant="primary"
            loading={translationIsLoading}
            disabled={translationIsLoading}
            key="confirm-translate"
          />
        ]}
      >
        <div className="mb-4 flex gap-2 items-center text-sm">
          <label
            htmlFor="translationInput"
            className={cn(
              'block text-gray-700 col-span-2 min-w-32',
              css(styles.userText)
            )}
          >
            {t('translation-name')}
          </label>
          <Input
            id="translationInput"
            placeholder={session?.filename}
            value={filenameInput}
            onChange={e => setFilenameInput(e.target.value)}
            className={cn('col-span-3')}
          />
        </div>

        <div className="mb-4 flex gap-2 items-center text-sm">
          <label
            htmlFor="languageSelect"
            className={cn(
              'block text-gray-700 max-w-fit col-span-2 min-w-32',
              css(styles.userText)
            )}
          >
            {t('translate-to')}
          </label>
          <SelectInput
            id="languageSelect"
            value={selectedLanguages}
            name="type"
            onChange={(selectedOptions: MultiValue<Option>) => {
              setSelectedLanguages(selectedOptions as Option[]);
              setSelectedGlossaryId(null);
            }}
            className="col-span-3 w-full"
            options={options}
            isMulti
          />
        </div>

        {matchingGlossaries.length > 0 && (
          <div className="mb-4 flex gap-2 items-center text-sm">
            <label
              htmlFor="glossarySelect"
              className={cn(
                'block text-gray-700 max-w-fit col-span-2 min-w-32',
                css(styles.userText)
              )}
            >
              {t('glossary')}
            </label>
            <SelectInput
              id="glossarySelect"
              value={
                selectedGlossaryId
                  ? {
                      label:
                        matchingGlossaries.find(
                          (g: Glossary) => g.id === selectedGlossaryId
                        )?.name || '',
                      value: selectedGlossaryId
                    }
                  : {
                      label: t('automatic'),
                      value: ''
                    }
              }
              name="glossary"
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onChange={(option: any) => {
                setSelectedGlossaryId(
                  option?.value ? Number(option.value) : null
                );
              }}
              className="col-span-3 w-full"
              options={[
                { label: t('automatic'), value: '' },
                ...matchingGlossaries.map((g: Glossary) => ({
                  label: `${g.name} (${g.source_language.toUpperCase()} → ${g.target_language.toUpperCase()})`,
                  value: g.id
                }))
              ]}
            />
          </div>
        )}

        {/* Translate to org default languages */}
        {orgDefaultLanguages.length > 0 && (
          <>
            <div
              className="my-3 border-t"
              style={{ borderColor: getColour('border') }}
            />
            <div className="flex items-center justify-between gap-2">
              <div>
                <p
                  className="text-xs font-medium"
                  style={{ color: getColour('normalText') }}
                >
                  {t('translate-to-org-languages')}
                </p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {orgDefaultLanguages.map(lang => (
                    <span
                      key={lang}
                      className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded-full uppercase"
                      style={{
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        color: '#3b82f6'
                      }}
                    >
                      {lang}
                    </span>
                  ))}
                </div>
              </div>
              <Button
                onClick={() => {
                  handleTranslateAll();
                  setModal('');
                }}
                label={t('translate-all')}
                size="small"
                variant="secondary"
                loading={translateAllLoading}
                disabled={translateAllLoading}
              />
            </div>
          </>
        )}
      </Modal>

      {linkModal && (
        <Modal
          isOpen={linkModal}
          onClose={() => setLinkModal(false)}
          size="medium"
          title={t('magic-link')}
          footerActions={[
            <Button
              onClick={() => setLinkModal(false)}
              label={t('close')}
              size="small"
              variant="smoke"
              key="close-button"
            />
          ]}
        >
          <div className="flex flex-col gap-4">
            {/* Existing magic link + copy */}
            <div className="flex items-center gap-2">
              <p
                className="break-all text-xs underline max-w-full"
                style={{
                  wordWrap: 'break-word',
                  color: getColour('normalText')
                }}
              >
                {link}
              </p>
              <button
                onClick={handleCopyLink}
                className="p-2 rounded-2xl transition"
                style={{ color: getColour('icon') }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor =
                    getColour('thirdLayer');
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                aria-label="Copy link"
              >
                <ClipboardCopy width={16} />
              </button>
            </div>

            <div style={{ borderTop: `1px solid ${getColour('border')}` }} />

            {/* New email share section */}
            <div className="flex flex-col gap-2">
              <h4
                className="text-sm font-medium"
                style={{ color: getColour('normalText') }}
              >
                {t('send-via-email') || 'Send via Email'}
              </h4>

              <div className="flex flex-col gap-1">
                <label
                  className="text-xs"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('email-addresses') ||
                    'Email addresses (comma or newline separated)'}
                </label>
                <textarea
                  value={emailInput}
                  onChange={e => handleEmailInputChange(e.target.value)}
                  rows={2}
                  placeholder={
                    t('enter-email-addresses') || 'Enter email addresses...'
                  }
                  className="w-full rounded-2xl px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary input-theme"
                  style={{
                    backgroundColor: getColour('secondLayer'),
                    borderColor: getColour('border'),
                    color: getColour('normalText'),
                    border: `1px solid ${getColour('border')}`
                  }}
                />
                {emailError && (
                  <p className="text-xs text-red-500">{emailError}</p>
                )}
              </div>

              <div className="flex flex-col gap-1 mt-2">
                <label
                  className="text-xs"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('optional-message-label') ||
                    'Add an optional message for recipients (optional)'}
                </label>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value.slice(0, 500))}
                  rows={3}
                  placeholder={
                    t('optional-message-placeholder') ||
                    'Add an optional message for recipients (optional)'
                  }
                  className="w-full rounded-2xl px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary input-theme"
                  style={{
                    backgroundColor: getColour('secondLayer'),
                    borderColor: getColour('border'),
                    color: getColour('normalText'),
                    border: `1px solid ${getColour('border')}`
                  }}
                />
                <div
                  className="flex items-center justify-between text-xs"
                  style={{ color: getColour('inactiveText') }}
                >
                  <span>
                    {t('characters-remaining-label', 'Characters remaining')}:{' '}
                    {remainingChars}
                  </span>

                  {lastEmailsSentCount != null && lastEmailsSentCount > 0 && (
                    <span className="text-green-600">
                      {t('magic-link-sent-to-recipients')}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex justify-end mt-2">
                <Button
                  label={t('send-email') || 'Send Email'}
                  variant="primary"
                  size="small"
                  icon={<Mail className="-ml-0.5 h-4 w-4" />}
                  onClick={handleSendEmail}
                  loading={isSendingEmail}
                  disabled={
                    isSendingEmail || !parsedEmails.length || !!emailError
                  }
                />
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};
